<template>
    <div :class="componentClasses">
        <div class="floating-beam-opened__avatar">
            <avatar-icon smaller round />

            <div class="floating-beam-opened__avatar-content">
                <div class="floating-beam-opened__avatar-title">
                    {{ assistent?.name }}
                </div>
                <div class="floating-beam-opened__avatar-text">
                    <div class="floating-beam-opened__avatar-chip">
                        AI
                    </div>
                    Digitale assistent
                </div>
            </div>

            <div
                class="floating-beam-opened__close-icon"
                @click="onClose"
                @keydown.enter="onClose"
            >
                <close-icon />
            </div>
        </div>

        <div class="floating-beam-opened__input">
            <delayed-chat-input @click="registerInteraction(VisitorInteractionType.WIDGET_CLICKED)" />
        </div>

        <div v-if="hasQuickOptions" class="floating-beam-opened__quick-options">
            <span>Snelle keuzes:</span>
            <floating-beam-quick-options />
        </div>
    </div>
</template>

<script setup lang="ts">
    import AvatarIcon from '@/components/avatar/AvatarIcon.vue';
    import FloatingBeamQuickOptions from '@/components/FloatingBeamQuickOptions.vue';
    import DelayedChatInput from '@/components/DelayedChatInput.vue';
    import useConfiguration from '@/composables/useConfiguration';
    import CloseIcon from '@keyvue/one-awesome-icons/icons/solid/xmark.svg?component';
    import { computed } from 'vue';
    import { Position, VisitorInteractionType } from '@keyvue/one-api-client';
    import useWidgetAppearance from '@/composables/useWidgetAppearance';
    import useVisitorData from '@/composables/useVisitorData';

    const { assistent } = useConfiguration();
    const { positionForIcon } = useWidgetAppearance();
    const { registerInteraction } = useVisitorData();

    const emit = defineEmits<{
        (e: 'close'): void;
    }>();

    function onClose() {
        emit('close');
    }

    const componentClasses = computed(() => {
        return {
            'floating-beam-opened': true,
            'floating-beam-opened--top': floatingBeamPositionTop.value,
            'floating-beam-opened--bottom': floatingBeamPositionBottom.value,
        };
    });

    const floatingBeamPositionTop = computed(() => {
        return positionForIcon.value?.equals(Position.TOP_LEFT) || positionForIcon.value?.equals(Position.TOP_RIGHT);
    });

    const floatingBeamPositionBottom = computed(() => {
        return positionForIcon.value?.equals(Position.BOTTOM_LEFT) || positionForIcon.value?.equals(Position.BOTTOM_RIGHT);
    });

    const hasQuickOptions = computed(() => {
        if (!assistent.value) {
            return false;
        }

        return assistent.value.widget.quickOptions.length > 0;
    });
</script>

<style scoped lang="scss">
    .floating-beam-opened {
        pointer-events: auto;
        position: fixed;
        width: 100vw;
        background-color: rgba(255, 255, 255, .9);
        display: flex;
        align-items: stretch;
        justify-content: center;
        gap: 1rem;
        padding: 1rem;
        flex-direction: column;

        &--mobile {
            min-height: 40vh;
            flex-direction: column;
            align-items: start;
        }

        &--top {
            top: 0;
        }

        &--bottom {
            bottom: 0;
        }

        &__avatar {
            display: flex;
            gap: .5rem;
            justify-content: space-between;
        }

        &__avatar-content {
            display: flex;
            flex-direction: column;
            justify-items: center;
            flex-grow: 1;
        }

        &__avatar-title {
            font-weight: bold;
        }

        &__avatar-text {
            display: flex;
            gap: 0.25rem;
            font-size: 70%;
            align-items: center;
        }

        &__avatar-chip {
            width: 1.25rem;
            height: 1.25rem;
            background-color: var(--color-widget);
            color: var(--color-widget-text);
            border-radius: .5rem;
            display: flex;
            justify-content: center;
            align-items: center;
            font-size: 70%;
            font-weight: bold;
        }

        &__close-icon {
            width: 1rem;
            height: 1rem;
            cursor: pointer;
            user-select: none;

            & > svg {
                color: rgba(0, 0, 0, 0.5);
            }
        }

        &__quick-options {
            display: flex;
            flex-direction: column;
            color: var(--color-widget);
            font-size: 80%;
        }
    }

    @media screen and (min-width: 768px) {
        .floating-beam-opened {
            flex-direction: row;
            align-items: center;
            justify-content: center;
            min-height: unset;

            &__avatar-content {
                display: none;
            }

            &__close-icon {
                display: none;
            }

            &__quick-options {
                flex-direction: row;
                align-items: center;
                gap: 0.5rem;
                overflow-x: scroll;
            }
        }
    }
</style>
